import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import OnlineBadge from '../components/OnlineBadge';
import BottomNav from '../components/BottomNav';
import ThemeToggle from '../components/ThemeToggle';
import BrandMark from '../components/BrandMark';
import { useSession } from '../auth/session';
import { refreshSnapshot } from '../db/snapshot';
import { loadSnapshot } from '../db/idb';
import { evaluateSchedule, DEFAULT_SCHEDULE_CONFIG, type ScheduleConfig } from '../lib/schedule';
import { capturedNowIso } from '../lib/serverTime';
import { pmsGroupLabel, groupBy } from '../lib/pmsGroup';

/**
 * Landing screen. Operators normally arrive via /t/<code> from an NFC tap and
 * never see this. The manual box + equipment list are the "tag unreadable" fallback.
 */
type KnownTag = {
  code: string; name: string; location: string; category: 'daily' | 'pms';
  pmsCycleDay: number | null; overdue: boolean; pmsGroup: string;
};

/** Contactless/NFC glyph — echoed at two sizes (FAB + sheet core). */
function ScanIcon({ size = 28 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M8.3 8.3a5 5 0 0 1 7.1 7.1" />
      <path d="M5.6 5.6a9 9 0 0 1 12.7 12.7" />
      <circle cx="6.8" cy="17.2" r="1.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

export default function ScanPrompt() {
  const nav = useNavigate();
  const session = useSession();
  const [code, setCode] = useState('');
  const [known, setKnown] = useState<KnownTag[]>([]);
  const [todayPmsCycleDay, setTodayPmsCycleDay] = useState<number | null>(null);
  const [view, setView] = useState<'daily' | 'pms'>('daily');
  const [scanOpen, setScanOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [nfcStatus, setNfcStatus] = useState<'idle' | 'scanning' | 'unsupported' | 'denied' | 'error'>('idle');
  const [nfcError, setNfcError] = useState('');
  const closeScan = () => { setScanOpen(false); setManualOpen(false); setCode(''); };

  useEffect(() => {
    if (!scanOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeScan(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [scanOpen]);

  // Web NFC (Chrome for Android only — no iOS/desktop support) — actually reads the tag while
  // the sheet is open, instead of just showing an animation and waiting for the OS-level tap
  // notification. Extracts the tag code from either a URL record (last path segment after /t/)
  // or a plain text record. Aborted automatically when the sheet closes.
  useEffect(() => {
    if (!scanOpen) return;
    const NDEFReaderCtor = (window as unknown as { NDEFReader?: new () => {
      scan: (opts?: { signal?: AbortSignal }) => Promise<void>;
      addEventListener: (type: 'reading' | 'readingerror', cb: (e: unknown) => void) => void;
    } }).NDEFReader;
    if (!NDEFReaderCtor) { setNfcStatus('unsupported'); return; }

    const controller = new AbortController();
    const reader = new NDEFReaderCtor();
    setNfcStatus('scanning');
    setNfcError('');

    reader.addEventListener('reading', (evt) => {
      const e = evt as { message?: { records?: Array<{ recordType: string; data?: DataView; encoding?: string }> } };
      const records = e.message?.records ?? [];
      let found = '';
      for (const r of records) {
        if (!r.data) continue;
        const text = new TextDecoder(r.encoding || 'utf-8').decode(r.data);
        if (r.recordType === 'url') {
          const m = text.match(/\/t\/([^/?#]+)/i);
          if (m) { found = decodeURIComponent(m[1]); break; }
        } else if (r.recordType === 'text' && !found) {
          found = text.trim();
        }
      }
      if (found) { closeScan(); open(found); }
    });
    reader.addEventListener('readingerror', () => {
      setNfcStatus('error');
      setNfcError('Could not read that tag. Try holding the phone steady against it for a moment.');
    });

    reader.scan({ signal: controller.signal }).catch((err: unknown) => {
      const name = (err as { name?: string })?.name;
      if (name === 'NotAllowedError') { setNfcStatus('denied'); setNfcError('NFC permission was denied. Allow it in your browser settings, or enter the code manually.'); }
      else if (name === 'AbortError') { /* sheet closed — expected */ }
      else { setNfcStatus('error'); setNfcError('Could not start NFC scanning on this device.'); }
    });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanOpen]);

  useEffect(() => {
    void refreshSnapshot().then(hydrate);
    hydrate();
    function hydrate() {
      loadSnapshot().then((s) => {
        if (!s) return;
        setTodayPmsCycleDay(s.todayPmsCycleDay ?? null);
        const byId = new Map(s.equipment.map((e) => [e.equipmentId, e]));
        const tplTitleById = new Map(s.templates.map((t) => [t.templateId, t.title]));
        const now = new Date(capturedNowIso());
        setKnown(s.tags.map((t) => {
          const e = byId.get(t.equipmentId);
          const cfg: ScheduleConfig = { ...DEFAULT_SCHEDULE_CONFIG, ...(e?.schedule ?? {}) };
          const sched = evaluateSchedule(now, e?.lastReadingAt ? new Date(e.lastReadingAt) : null, cfg);
          const tplTitle = e ? tplTitleById.get(e.templateId) : undefined;
          return {
            code: t.code, name: e?.name ?? t.equipmentId, location: e?.location ?? '', category: e?.category ?? 'daily',
            pmsCycleDay: e?.pmsCycleDay ?? null, overdue: sched.status === 'OVERDUE',
            pmsGroup: pmsGroupLabel(tplTitle ?? '') || 'Other',
          };
        }));
      }).catch(() => {});
    }
  }, []);

  const dailyCount = known.filter((k) => k.category === 'daily').length;
  const pmsAll = known.filter((k) => k.category === 'pms');
  // Today's PMS queue = items whose rotation slot is today, anything overdue that slipped
  // past its slot, and anything not yet assigned a slot — so nothing is silently hidden.
  const pmsQueue = pmsAll.filter((k) => k.pmsCycleDay === todayPmsCycleDay || k.overdue || k.pmsCycleDay == null);
  const pmsUnscheduled = pmsAll.filter((k) => k.pmsCycleDay == null);
  const pmsCount = pmsQueue.length;
  const shown = view === 'daily' ? known.filter((k) => k.category === 'daily') : pmsQueue;
  const pmsGroups = groupBy(pmsQueue, (k) => k.pmsGroup);

  const open = (c: string) => {
    const v = c.trim().toUpperCase();
    if (v) nav(`/t/${encodeURIComponent(v)}`);
  };

  return (
    <div className="app has-bottomnav">
      <div className="spread">
        <BrandMark />
        <ThemeToggle />
      </div>
      <OnlineBadge />

      {known.length > 0 && (
        <>
          <h2>Equipment</h2>
          <div className="choice-group" role="tablist" aria-label="Equipment view" style={{ marginBottom: 12 }}>
            <button type="button" className={`choice-btn ${view === 'daily' ? 'on' : ''}`} role="tab" aria-selected={view === 'daily'} onClick={() => setView('daily')}>
              Daily readings{dailyCount ? ` (${dailyCount})` : ''}
            </button>
            <button type="button" className={`choice-btn ${view === 'pms' ? 'on' : ''}`} role="tab" aria-selected={view === 'pms'} onClick={() => setView('pms')}>
              PMS today{pmsCount ? ` (${pmsCount})` : ''}
            </button>
          </div>
          {view === 'pms' && pmsAll.length > 0 && (
            <p className="muted tiny" style={{ marginTop: -6, marginBottom: 12 }}>
              Showing only today's slot in the PMS rotation{pmsAll.some((k) => k.overdue) ? ', plus anything overdue' : ''}.
            </p>
          )}
          {shown.length === 0 && view === 'daily' && <p className="muted center tiny">Nothing here yet.</p>}
          {shown.length === 0 && view === 'pms' && (
            <p className="muted center tiny">
              Nothing due today{pmsAll.length ? ' — the rest of the PMS roster is scheduled on other days.' : '.'}
            </p>
          )}
          {view === 'daily' && (
            <div className="cardlist">
              {shown.map((k) => <EquipCard key={k.code} k={k} onOpen={open} view={view} todayPmsCycleDay={todayPmsCycleDay} />)}
            </div>
          )}
          {view === 'pms' && pmsGroups.map((g) => (
            <div key={g.label} style={{ marginBottom: 4 }}>
              <h3 className="pmsGroup-head">{g.label} <span className="muted">({g.items.length})</span></h3>
              <div className="cardlist">
                {g.items.map((k) => <EquipCard key={k.code} k={k} onOpen={open} view={view} todayPmsCycleDay={todayPmsCycleDay} />)}
              </div>
            </div>
          ))}
          {view === 'pms' && pmsUnscheduled.length > 0 && (
            <p className="muted tiny" style={{ marginTop: 10 }}>
              {pmsUnscheduled.length} PMS item{pmsUnscheduled.length === 1 ? '' : 's'} shown because {pmsUnscheduled.length === 1 ? "it isn't" : "they aren't"} assigned a rotation day yet — an admin can set one in Equipment.
            </p>
          )}
        </>
      )}

      {session?.role === 'admin' && (
        <Link className="btn secondary" to="/admin" style={{ textDecoration: 'none', marginTop: 18 }}>← Back to admin panel</Link>
      )}
      {!session && (
        <p className="center muted tiny" style={{ marginTop: 18 }}>You'll be asked to sign in when you enter a reading.</p>
      )}

      <button className="fab" aria-label="Scan NFC tag" onClick={() => setScanOpen(true)}>
        <ScanIcon />
      </button>

      {scanOpen && (
        <div className="scanSheetWrap" onClick={(e) => e.target === e.currentTarget && closeScan()}>
          <div className="scanSheet" role="dialog" aria-modal="true" aria-label="Scan NFC tag">
            <div className="scanSheet-handle" />
            <button type="button" className="scanSheet-close" aria-label="Close" onClick={closeScan}>✕</button>

            <div className="scanRadar">
              <span className="scanRing" />
              <span className="scanRing" />
              <span className="scanRing" />
              <div className="scanCore"><ScanIcon size={38} /></div>
            </div>

            {nfcStatus === 'scanning' && (
              <>
                <p className="scanSheet-title">Ready to scan</p>
                <p className="muted" style={{ margin: 0 }}>Hold your phone flat against the NFC tag on the equipment.</p>
              </>
            )}
            {nfcStatus === 'unsupported' && (
              <>
                <p className="scanSheet-title">In-app scanning isn't available here</p>
                <p className="muted" style={{ margin: 0 }}>
                  This browser doesn't support in-app NFC scanning (only Chrome on Android does). Just tap your phone
                  against the tag directly — your phone will show a notification to open the logbook, or enter the code below.
                </p>
              </>
            )}
            {(nfcStatus === 'denied' || nfcStatus === 'error') && (
              <>
                <p className="scanSheet-title">Scan didn't work</p>
                <p className="muted" style={{ margin: 0 }}>{nfcError}</p>
              </>
            )}

            {!manualOpen ? (
              <button type="button" className="scanSheet-manual" onClick={() => setManualOpen(true)}>
                No tag? Enter code manually
              </button>
            ) : (
              <form
                className="scanSheet-manualForm"
                onSubmit={(e) => { e.preventDefault(); closeScan(); open(code); }}
              >
                <input
                  autoFocus placeholder="Tag code, e.g. BBB-001" autoCapitalize="characters"
                  value={code} onChange={(e) => setCode(e.target.value)}
                />
                <button type="submit" disabled={!code.trim()}>Open logbook</button>
              </form>
            )}
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

function EquipCard({ k, onOpen, view, todayPmsCycleDay }: {
  k: KnownTag; onOpen: (code: string) => void; view: 'daily' | 'pms'; todayPmsCycleDay: number | null;
}) {
  return (
    <button className="secondary" style={{ justifyContent: 'flex-start', textAlign: 'left', padding: 14 }} onClick={() => onOpen(k.code)}>
      <span>
        <span style={{ fontWeight: 700 }}>{k.name}</span>
        <span className="muted" style={{ display: 'block', fontWeight: 400, fontSize: '.85rem' }}>
          {k.location ? `${k.location} · ` : ''}{k.code}
        </span>
        {view === 'pms' && k.pmsCycleDay == null && (
          <span className="muted" style={{ display: 'block', fontSize: '.78rem' }}>Not yet scheduled</span>
        )}
        {view === 'pms' && k.overdue && k.pmsCycleDay !== todayPmsCycleDay && k.pmsCycleDay != null && (
          <span style={{ color: 'var(--bad)', display: 'block', fontSize: '.78rem', fontWeight: 600 }}>Overdue — missed slot</span>
        )}
      </span>
    </button>
  );
}
